import React, { useState } from "react";
import { Alert, Button, Input, Statistic } from "antd";
import toast from "react-hot-toast";
import { toastErrorStyle, toastSuccessStyle } from "src/constants";
import {
  useProcesarPagoBacSimplePayMutation,
  useReenviarOtpBacSimplePayMutation,
} from "src/services/cartServices";
import { formatStrapiTemplate } from "../../../utils/strapi-template";
import { ActionButtons, CountdownRow, FieldError, OtpInputRow } from "./bac-simple-pay-form.styled";

const MAX_OTP_RESENDS = 2;

interface BacOtpStepProps {
  content?: Record<string, string | undefined> | null;
  preregisterId: string;
  expiresAt: number;
  resendCount: number;
  onResendCountChange: (count: number) => void;
  onExpire: () => void;
  onCancel: () => void;
  onSuccess: (pagoId: number) => void;
  onRestart: () => void;
}

function BacOtpStep({
  content,
  preregisterId,
  expiresAt,
  resendCount,
  onResendCountChange,
  onExpire,
  onCancel,
  onSuccess,
  onRestart,
}: BacOtpStepProps) {
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState<string | undefined>(undefined);
  const [isTerminal, setIsTerminal] = useState(false);
  const [terminalMessage, setTerminalMessage] = useState<string | undefined>(undefined);

  const [procesarPago, { isLoading: isProcesando }] = useProcesarPagoBacSimplePayMutation();
  const [reenviarOtp, { isLoading: isReenviando }] = useReenviarOtpBacSimplePayMutation();

  const handleResend = async () => {
    const result: any = await reenviarOtp(preregisterId);
    if (result?.data?.responseStatus === "Success") {
      onResendCountChange(resendCount + 1);
      toast.success(content?.otp_resend_success || "Se reenvió el código por SMS", toastSuccessStyle);
    } else {
      const message =
        result?.error?.data?.message ||
        result?.data?.message ||
        content?.otp_resend_error ||
        "No fue posible reenviar el código";
      toast.error(message, toastErrorStyle);
    }
  };

  const handleSubmit = async () => {
    if (otp.length !== 6) {
      setOtpError(content?.otp_code_length_error || "El código debe tener 6 dígitos");
      return;
    }

    const result: any = await procesarPago({ preregisterId, otpCode: otp });
    const data = result?.data?.data;

    if (result?.data?.responseStatus === "Success" && data?.estado === "Aprobado") {
      onSuccess(data.pagoId);
      return;
    }

    if (data?.estado === "Rechazado") {
      setIsTerminal(true);
      setTerminalMessage(
        data.mensaje || content?.payment_rejected_message || "El pago fue rechazado",
      );
      return;
    }

    const mensaje =
      data?.mensaje ||
      result?.error?.data?.message ||
      result?.data?.message ||
      content?.otp_incorrect_message ||
      "Código incorrecto, intente de nuevo";
    setOtpError(mensaje);
    setOtp("");
  };

  if (isTerminal) {
    return (
      <>
        <Alert type="error" showIcon message={terminalMessage} style={{ marginBottom: 20 }} />
        <ActionButtons>
          <Button className="cancel-btn" size="large" onClick={onCancel}>
            {content?.cancel_button || "Cancelar"}
          </Button>
          <Button className="pay-btn" type="primary" size="large" onClick={onRestart}>
            {content?.restart_button || "Iniciar de nuevo"}
          </Button>
        </ActionButtons>
      </>
    );
  }

  const resendLabel =
    resendCount >= MAX_OTP_RESENDS
      ? content?.otp_max_resends_message || "Máximo de reenvíos alcanzado"
      : formatStrapiTemplate(content?.otp_resend_button, "Reenviar código (quedan {count})", {
          count: MAX_OTP_RESENDS - resendCount,
        });

  return (
    <>
      <CountdownRow>
        <Statistic.Countdown
          title={content?.remaining_time_label || "Tiempo restante"}
          value={expiresAt}
          onFinish={onExpire}
        />
      </CountdownRow>

      <Alert
        type="info"
        showIcon
        message={
          content?.otp_sent_message ||
          "Le enviamos un código de verificación de 6 dígitos por SMS. Ingréselo para confirmar el pago."
        }
      />

      <Alert
        type="warning"
        showIcon
        style={{ marginTop: 10 }}
        message={
          content?.do_not_close_warning ||
          "No cierre ni actualice esta ventana hasta que el pago se confirme como completado. Si lo hace, perderá la referencia para validarlo y deberá iniciar el proceso de nuevo."
        }
      />

      <OtpInputRow>
        <Input.OTP
          length={6}
          value={otp}
          onChange={(value: string) => {
            setOtp(value);
            if (otpError) setOtpError(undefined);
          }}
        />
      </OtpInputRow>
      {otpError && <FieldError style={{ textAlign: "center" }}>{otpError}</FieldError>}

      <ActionButtons style={{ justifyContent: "space-between" }}>
        <Button
          onClick={handleResend}
          loading={isReenviando}
          disabled={resendCount >= MAX_OTP_RESENDS}
        >
          {resendLabel}
        </Button>
        <div style={{ display: "flex", gap: 12 }}>
          <Button className="cancel-btn" size="large" onClick={onCancel} disabled={isProcesando}>
            {content?.cancel_button || "Cancelar"}
          </Button>
          <Button
            className="pay-btn"
            type="primary"
            size="large"
            loading={isProcesando}
            onClick={handleSubmit}
          >
            {content?.confirm_button || "Confirmar"}
          </Button>
        </div>
      </ActionButtons>
    </>
  );
}

export default BacOtpStep;
